import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { faHandHoldingBox, faTruck, faWarehouse } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';

import { call } from '@spacefill/shared/src/utils/Backend';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';
import { CommandSelect } from '@spacefill/uikit/src/components/CommandSelect/CommandSelect';
import { Loader } from '@spacefill/uikit/src/components/Loader';
import { safeLocalStorage } from '@spacefill/uikit/src/utils/storage';

import { RECENT_CONTEXT_SWITCHES_KEY, handleContextSwitch } from '../utils/contextSwitch';

function normalizeForSearch(str) {
    return String(str || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

function loadRecentSwitches() {
    try {
        const raw = safeLocalStorage.getItem(RECENT_CONTEXT_SWITCHES_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function saveRecentSwitches(items) {
    try {
        safeLocalStorage.setItem(RECENT_CONTEXT_SWITCHES_KEY, JSON.stringify(items));
    } catch {
        // silent catch
    }
}

function extractSwitchValue(optionLike) {
    if (!optionLike) return null;
    if (optionLike.type && optionLike.id !== undefined && optionLike.id !== null) return optionLike;
    if (
        optionLike.value &&
        optionLike.value.type &&
        optionLike.value.id !== undefined &&
        optionLike.value.id !== null
    ) {
        return optionLike.value;
    }
    return null;
}

function toCommandSelectId(switchValue) {
    if (!switchValue) return null;
    return `${switchValue.type}:${switchValue.id}`;
}

function toCommandSelectLabel(switchValue) {
    if (!switchValue) return '';
    return (
        <div className='flex items-center gap-2'>
            {switchValue.type === 'LSP' && <FontAwesomeIcon icon={faWarehouse} />}
            {switchValue.type === 'CUSTOMER' && <FontAwesomeIcon icon={faHandHoldingBox} />}
            {switchValue.type === 'CARRIER' && <FontAwesomeIcon icon={faTruck} />} {switchValue.name}
        </div>
    );
}

export function AdminCompanySwitcher() {
    const { user } = useUserContext();
    const currentSelectId = user?.customerId
        ? `CUSTOMER:${user.customerId}`
        : user?.logisticProviderId
          ? `LSP:${user.logisticProviderId}`
          : user?.carrierId
            ? `CARRIER:${user.carrierId}`
            : null;
    const currentAccountName =
        user?.customer?.company || user?.logisticProvider?.company || user?.carrier?.company || null;
    const [inputQuery, setInputQuery] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const t = setTimeout(() => {
            setSearchQuery(inputQuery);
        }, 250);
        return () => clearTimeout(t);
    }, [inputQuery]);

    const { data: dataCustomerLsp, isFetching } = useQuery({
        queryKey: ['admin/list-customers-lsp', searchQuery],
        queryFn: async () => call('admin/list-customers-lsp', { name: searchQuery }, 'POST'),
        keepPreviousData: true,
    });

    const [recentSwitches, setRecentSwitches] = useState(() => loadRecentSwitches().slice(0, 3));

    const [showSpinner, setShowSpinner] = useState(false);
    useEffect(() => {
        const busy = loading || isFetching;
        if (!busy) {
            setShowSpinner(false);
            return;
        }
        const t = setTimeout(() => setShowSpinner(true), 200);
        return () => clearTimeout(t);
    }, [loading, isFetching]);

    const items = useMemo(() => {
        const q = normalizeForSearch(inputQuery);

        const recentOptions = (recentSwitches || [])
            .map((sv) => extractSwitchValue(sv) || sv)
            .filter((sv) => {
                if (!q) return true;
                return normalizeForSearch(toCommandSelectLabel(sv)).includes(q);
            })
            .slice(0, 3)
            .map((sv) => ({
                id: toCommandSelectId(sv),
                label: toCommandSelectLabel(sv),
                switchValue: sv,
            }))
            .filter((o) => o.id);

        const recentIdSet = new Set(recentOptions.map((o) => o.id));

        const remoteOptionsRaw = (dataCustomerLsp || [])
            .map((raw) => {
                const sv = extractSwitchValue(raw);
                if (!sv) return null;
                const id = toCommandSelectId(sv);
                if (!id) return null;
                return {
                    id,
                    label: toCommandSelectLabel(sv),
                    switchValue: sv,
                };
            })
            .filter(Boolean)
            .filter((o) => !recentIdSet.has(o.id));

        const nextItems = [];
        if (recentOptions.length > 0) {
            nextItems.push({ type: 'group-title', label: 'Récents' }, ...recentOptions);
        }
        nextItems.push({ type: 'group-title', label: 'Résultats' }, ...remoteOptionsRaw);

        if (currentSelectId && currentAccountName && !nextItems.some((o) => o.id === currentSelectId)) {
            const [type, id] = currentSelectId.split(':');
            nextItems.push({
                id: currentSelectId,
                label: toCommandSelectLabel({ type, id, name: currentAccountName }),
                switchValue: { type, id, name: currentAccountName },
            });
        }

        return nextItems;
    }, [dataCustomerLsp, recentSwitches, inputQuery, currentSelectId, currentAccountName]);

    return (
        <CommandSelect
            value={currentSelectId}
            label='Choisir un Customer/LSP'
            fullWidth
            icon={showSpinner ? <Loader size='small' /> : undefined}
            items={items}
            searchable
            maxHeight={420}
            searchPlaceholder='Chercher...'
            autoFocusSearchOnOpen
            emptyText='Aucun résultat'
            onSearchChange={(query) => {
                setInputQuery(query);
            }}
            onChange={async (_value, option) => {
                if (!option) return;
                const sv = option.switchValue ?? null;
                if (!sv) return;
                setRecentSwitches((prev) => {
                    const prevList = Array.isArray(prev) ? prev : [];
                    const next = [sv, ...prevList.filter((x) => toCommandSelectId(x) !== toCommandSelectId(sv))].slice(
                        0,
                        3
                    );
                    saveRecentSwitches(next);
                    return next;
                });
                await handleContextSwitch({
                    switchValue: sv,
                    setLoading,
                    currentPathname: location.pathname,
                });
            }}
        />
    );
}
